import {
  FlexRow,
  SectionContainer,
  SectionInner,
  TextContainer,
} from "components/rhombus-UI/theme/containers";
import React from "react";
import { styled } from "@linaria/react";
import {
  MainParagraph,
  SectionTitle,
  TitleSmall,
} from "components/rhombus-UI/theme/typography";
import TestimonialCard from "./TestimonialCard";
import RhombusButton from "components/common/RhombusButton";
import { CTA_BUTTON_COPY, CTA_LINK_3 } from "components/data/landing-page-data";
import wenTestimony from "./assets/wen-testimony.png";
import storQuestLogo from "./assets/storquest-logo.png";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { css } from "@linaria/core";

const CardContainer = styled(FlexRow)`
  gap: 5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Card = styled(FlexRow)`
  max-width: 648px;
  width: calc(50% - 5rem);
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  border-radius: 1rem;
  background-color: ${(props) => props.color};
  .text {
    flex-grow: 1;
    padding: 28px;
    h3 {
      font-size: 32px;
    }
    p {
      font-size: 18px;
    }
  }
  @media (max-width: 1350px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    .text {
      h3 {
        font-size: 24px;
      }
      p {
        font-size: 16px;
      }
    }
  }
`;

const imageStyles = css`
  width: 100%;
  max-width: 324px;
  height: 324px;
  @media (max-width: 600px) {
    max-width: unset;
  }
`;

const AllInOneF = ({
  title = "All-In-One",
  bgColor = "var(--white)",
  cardColor = "var(--nuetral-100)",
  cards = null,
  showTestimonial = false,
  showCTA = false,
}) => {
  const imageData = useStaticQuery(graphql`
    query {
      card1: file(
        relativePath: {
          eq: "components/landing-pages/surveillance/version-f/assets/video.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card2: file(
        relativePath: {
          eq: "components/landing-pages/surveillance/version-f/assets/access-control.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card3: file(
        relativePath: {
          eq: "components/landing-pages/surveillance/version-f/assets/integrations.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card4: file(
        relativePath: {
          eq: "components/landing-pages/surveillance/version-f/assets/remote-management.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);

  const defaultCards = [
    {
      image: imageData.card1,
      title: "Intelligent Video Surveillance",
      blurb:
        "High-quality video with automatic incident detection and automated alerts.",
    },
    {
      image: imageData.card2,
      title: "Access Control",
      blurb: "Real-time permissions updates and role-based access control.",
    },
    {
      image: imageData.card3,
      title: "Integration & API",
      blurb:
        "Plug-and-play integrations and an open API. Works with your existing tools & systems.",
    },
    {
      image: imageData.card4,
      title: "Remote Management",
      blurb: "Deploy and manage unlimited locations remotely.",
    },
  ];

  const _cards = cards ?? defaultCards;

  return (
    <SectionContainer color={bgColor}>
      <SectionInner className="flex-col" style={{ gap: "5rem" }}>
        <SectionTitle>{title}</SectionTitle>
        <CardContainer>
          {_cards.map((card, index) => (
            <Card color={cardColor} key={index}>
              <GatsbyImage
                image={getImage(card.image)}
                alt={card.title}
                loading="lazy"
                className={imageStyles}
                objectFit="cover"
              />
              <TextContainer className="text">
                <TitleSmall>{card.title}</TitleSmall>
                <MainParagraph>{card.blurb}</MainParagraph>
              </TextContainer>
            </Card>
          ))}
        </CardContainer>
        {showCTA && (
          <RhombusButton
            type="primary-dark"
            title={CTA_BUTTON_COPY}
            path={CTA_LINK_3}
          />
        )}
        {showTestimonial && (
          <TestimonialCard
            cardColor={cardColor}
            stat1={{ value: "30%", label: "reduction in break-ins" }}
            stat2={{ value: "90%", label: "faster incident response" }}
            quote="One of the key reasons we chose Rhombus and cloud-based video monitoring was the shift from a reactive camera system—where we could only review footage after an incident—to a proactive solution."
            author={{
              title: "Manager of Data Intelligence & Strategy",
              name: "Wenxuan Fan",
              company: "StorQuest Self Storage",
            }}
            companyLogo={{ src: storQuestLogo, width: 236, height: 52 }}
            testimonialImage={wenTestimony}
          />
        )}
      </SectionInner>
    </SectionContainer>
  );
};

export default AllInOneF;
